import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatChipInputEvent, MatDialog } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastsManager } from 'ng2-toastr';
import { Subscription } from 'rxjs/Subscription';
import { CommentIface } from '../../../interfaces/comment.iface';
import { Discount } from '../../../interfaces/discount';
import { OfferIface } from '../../../interfaces/offer.iface';
import { OfferFeature } from '../../../interfaces/offerFeature';
import { SalesChannelIface } from '../../../interfaces/sales-channel.iface';
import { Status } from '../../../interfaces/status';
import { AuthService } from '../../../services/auth.service';
import { LoggerService } from '../../../services/logger.service';
import { OffersService } from '../../../services/offers.service';
import { SalesChannelService } from '../../../services/sales-channel.service';
import { StatusService } from '../../../services/status.service';
import { CommentSelectDialogComponent } from './comment-select-dialog/comment-select-dialog.component';
import { DiscountSelectDialogComponent } from './discount-select-dialog/discount-select-dialog.component';
import { ProductSelectDialogComponent } from './product-select-dialog/product-select-dialog.component';

@Component({
  selector: 'app-offer-edit',
  templateUrl: './offer-edit.component.html',
  styleUrls: ['./offer-edit.component.scss']
})
export class OfferEditComponent implements OnInit, OnDestroy {
  @Input()
  public offer: OfferIface;

  public offerForm: FormGroup;
  public statusesWithEditPermForRoles: Status[];
  public statuses: Status[];
  public customTags: string[];
  public availableInLocations: string[];
  public availableInChannels: string[];
  public availableForCustomerSegments: string[];
  public discounts: Discount[];
  public comments: CommentIface[];
  public includedServices: OfferFeature[];
  public allAvailableChannels: string[];
  private offerId = '';
  private userRoles: string[] = [];
  private subscription: Subscription;
  public isOfferLoading: boolean = false;
  public isSubmitting = false;

  static initForm() {
    return new FormGroup({
      name: new FormControl('', [Validators.required]),
      code: new FormControl(''),
      offerType: new FormControl('', [Validators.required]),
      offerStatus: new FormControl('', [Validators.required]),
      notesForSales: new FormControl('', []),
      loyaltyPeriodInMths: new FormControl('', [Validators.required]),
      billingPeriodInMths: new FormControl('', [Validators.required]),
      activationPrice: new FormControl('', [Validators.required]),
      billingPeriodPriceDuringLoyalty: new FormControl('', [Validators.required]),
      billingPeriodPriceAfterLoyalty: new FormControl('', [Validators.required]),
      linkToTermsOfService: new FormControl('', [Validators.required]),
      availableSince: new FormControl('', [Validators.required]),
      availableUntil: new FormControl(''),
      availableInChannels: new FormControl('')
    });
  }

  static getInputDateFormat(date: Date): string {
    if (!date) {
      return '';
    }
    const actualMonth = date.getMonth() + 1;
    const day = date.getDate().toString().length === 1 ? `0${date.getDate()}` : date.getDate();
    const month = actualMonth.toString().length === 1 ? `0${actualMonth}` : actualMonth;
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }

  ifUserCanEdit(): boolean {
    if (this.isNewOffer(this.offerId)) {
      return true;
    } else if (this.offer && this.statusesWithEditPermForRoles) {
      return this.statusesWithEditPermForRoles.some(status => status.statusCode === this.offer.offerStatus);
    }
    return false;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private offerService: OffersService,
    private statusService: StatusService,
    private authService: AuthService,
    public dialog: MatDialog,
    private toastr: ToastsManager,
    private salesChannelService: SalesChannelService,
    public translate: TranslateService
  ) {
    this.offerForm = OfferEditComponent.initForm();
    this.userRoles = this.authService.user.roles || [];
  }

  selectStep(chosenStatus: string): number {
    if (chosenStatus.length) {
      const statuses = this.statuses.filter(status => {
        return chosenStatus === status.statusCode;
      });
      return statuses[0].orderNumber;
    }
  }

  setOfferStatusValue(e): void {
    const matchedStatus = this.statusesWithEditPermForRoles.filter(status => e.selectedIndex === status.orderNumber);
    if (matchedStatus[0].statusCode !== this.offerForm.controls.offerStatus.value) {
      this.offerForm.patchValue({ offerStatus: matchedStatus[0].statusCode });
    }
  }

  private isStatusEditableByUser(status: Status, userRoles: string[]): boolean {
    const matchingRoles = status.rolesAllowedToEditItemsAtThisStatus.filter(roleWithEditPerm => {
      return userRoles.includes(roleWithEditPerm);
    });
    return matchingRoles.length > 0;
  }

  ngOnInit() {
    this.getSalesChannelsList();
    this.offerId = this.route.snapshot.paramMap.get('id');
    this.statusService.getStatuses().subscribe(statuses => {
      this.statuses = statuses.sort((a, b) =>
        a.orderNumber < b.orderNumber ? -1 : a.orderNumber > b.orderNumber ? 1 : 0
      );
      const offerStatus = this.offerForm.get('offerStatus').value;
      this.setValidationForOfferCode(offerStatus);
      this.setObservableForOfferStatusChange();
      this.statusesWithEditPermForRoles = this.statuses.filter(status => {
        return this.isStatusEditableByUser(status, this.userRoles);
      });
    });

    if (!this.isNewOffer(this.offerId)) {
      this.isOfferLoading = true;

      this.offerService.findById(this.offerId).subscribe(
        offer => {
          this.offer = offer;
          this.offerForm.patchValue({
            name: offer.name,
            code: offer.code,
            offerType: offer.offerType,
            offerStatus: offer.offerStatus,
            notesForSales: offer.notesForSales,
            loyaltyPeriodInMths: offer.loyaltyPeriodInMths,
            billingPeriodInMths: offer.billingPeriodInMths,
            activationPrice: offer.activationPrice,
            billingPeriodPriceDuringLoyalty: offer.billingPeriodPriceDuringLoyalty,
            billingPeriodPriceAfterLoyalty: offer.billingPeriodPriceAfterLoyalty,
            linkToTermsOfService: offer.linkToTermsOfService,
            availableInChannels: offer.availableInChannels
          });

          const availableSince = new Date(offer.availableSince);
          this.offerForm.controls['availableSince'].setValue(
            `${OfferEditComponent.getInputDateFormat(availableSince)}`
          );

          if (offer.availableUntil) {
            const availableUntil = new Date(offer.availableUntil);
            this.offerForm.controls['availableUntil'].setValue(
              `${OfferEditComponent.getInputDateFormat(availableUntil)}`
            );
          }

          this.customTags = offer.tags.filter(tag => tag.category === 'custom').map(tag => tag.value);
          this.availableInLocations = offer.availableInLocations;
          this.availableInChannels = offer.availableInChannels;
          this.availableForCustomerSegments = offer.availableForCustomerSegments;
          this.discounts = offer.discounts;
          this.comments = offer.comments || [];
          this.includedServices = offer.includedServices;
          this.isOfferLoading = false;
        },
        error => {
          LoggerService.log('OfferEditComponent::ngOnInit::findById', error);
          this.toastr.error(this.translate.instant('EDIT_OFFER.COMPONENT.DATA_ERROR'));
          this.isOfferLoading = false;
        }
      );
    } else {
      this.customTags = [];
      this.availableInLocations = ['ALL_CITIES'];
      this.availableInChannels = ['ALL_CHANNELS'];
      this.availableForCustomerSegments = ['ALL_SEGMENTS'];
      this.discounts = [];
      this.includedServices = [];
      this.comments = [];
      this.setTemplateToForm();
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  getSalesChannelsList(): void {
    this.salesChannelService.getChannels().subscribe((channels: SalesChannelIface[]) => {
      this.allAvailableChannels = channels.map(channel => channel.name);
    });
  }

  private isNewOffer(offerId: string): boolean {
    return !offerId || offerId === 'new';
  }

  setObservableForOfferStatusChange(): void {
    this.subscription = this.offerForm.get('offerStatus').valueChanges.subscribe((offerStatus: string) => {
      this.setValidationForOfferCode(offerStatus);
    });
  }

  setValidationForOfferCode(offerStatus): void {
    if (!offerStatus) {
      return;
    }
    const status = this.statuses.find(s => s.statusCode === offerStatus);
    if (status && status.isCodeRequired) {
      this.offerForm.controls['code'].setValidators([<ValidatorFn>Validators.required]);
      this.offerForm.controls['code'].updateValueAndValidity();
    } else {
      this.offerForm.controls['code'].setValidators([]);
      this.offerForm.controls['code'].updateValueAndValidity();
    }
  }

  setTemplateToForm(): void {
    const template = this.offerService.getOfferTemplate;
    if (template) {
      this.offerForm.patchValue(template.offerFormControls);
      this.customTags = template.customTags;
      this.includedServices = template.includedServices;
      this.availableInLocations = template.availableInLocations;
      this.availableInChannels = template.availableInChannels;
      this.availableForCustomerSegments = template.availableForCustomerSegments;
      this.discounts = template.discounts;
    }
  }

  setOfferTemplate(form: FormGroup): void {
    this.offerService.setOfferTemplate = {
      offerFormControls: form.getRawValue(),
      customTags: this.customTags,
      includedServices: this.includedServices,
      availableInLocations: this.availableInLocations,
      availableInChannels: this.availableInChannels,
      availableForCustomerSegments: this.availableForCustomerSegments,
      discounts: this.discounts
    };
    this.toastr.info(this.translate.instant('EDIT_OFFER.COMPONENT.NEW_TEMPLATE'));
  }

  addTag(event: MatChipInputEvent, array: string[]): void {
    const input = event.input;
    const value = event.value;

    if ((value || '').trim()) {
      array.push(value.trim());
    }

    if (input) {
      input.value = '';
    }
  }

  removeTag(tag: any, array): void {
    const index = array.indexOf(tag);

    if (index >= 0) {
      array.splice(index, 1);
    }
  }

  deleteElementFromArray(array: any[], indexToRemove: number): void {
    if (indexToRemove >= 0) {
      array.splice(indexToRemove, 1);
    }
  }

  saveOffer(offerForm: FormGroup) {
    if (this.offerForm.invalid) {
      return this.toastr.warning(this.translate.instant('EDIT_OFFER.COMPONENT.MISSING_FIELDS'));
    } else if (!this.includedServices.length) {
      return this.toastr.warning(this.translate.instant('EDIT_OFFER.COMPONENT.NO_PRODUCTS_IN_OFFER'));
    }

    const currentOfferTags = this.offer ? this.offer.tags.filter(t => t.category !== 'custom') : [];
    const offer: OfferIface = {
      name: offerForm.value.name,
      code: offerForm.value.code,
      offerType: offerForm.value.offerType,
      offerStatus: offerForm.value.offerStatus,
      notesForSales: offerForm.value.notesForSales,
      metadata: this.offer ? this.offer.metadata : {},
      loyaltyPeriodInMths: offerForm.value.loyaltyPeriodInMths,
      billingPeriodInMths: offerForm.value.billingPeriodInMths,
      activationPrice: offerForm.value.activationPrice,
      billingPeriodPriceDuringLoyalty: offerForm.value.billingPeriodPriceDuringLoyalty,
      billingPeriodPriceAfterLoyalty: offerForm.value.billingPeriodPriceAfterLoyalty,
      linkToTermsOfService: offerForm.value.linkToTermsOfService,
      availableSince: this.offerForm.value.availableSince,
      availableUntil: this.offerForm.value.availableUntil,
      availableInLocations: this.availableInLocations,
      availableInChannels: offerForm.value.availableInChannels,
      availableForCustomerSegments: this.availableForCustomerSegments,
      discounts: this.discounts,
      comments: this.comments,
      includedServices: this.includedServices,
      minOptionalServices: 0,
      tags: [
        ...currentOfferTags,
        ...this.customTags.map(tag => {
          return {
            value: tag,
            category: 'custom'
          };
        })
      ],
      summaryDiscountDuringLoyalty: this.includedServices.reduce((sum, service) => {
        return sum + service.priceDiscountDuringLoyalty;
      }, 0)
    };

    if (!this.isNewOffer(this.offerId)) {
      this.isSubmitting = true;

      this.offerService.update(this.offerId, offer).subscribe(
        savedOffer => {
          this.offer = savedOffer;
          this.toastr.info(this.translate.instant('EDIT_OFFER.COMPONENT.OFFER_UPDATED'));
          this.router.navigate(['offers']);
          this.isSubmitting = false;
        },
        error => {
          LoggerService.log('OfferEditComponent::error updating offer', error);
          this.toastr.error(this.translate.instant('EDIT_OFFER.COMPONENT.OFFER_UPDATE_PROBLEM'));
          this.isSubmitting = false;
        }
      );
    } else {
      this.isSubmitting = true;

      this.offerService.create(offer).subscribe(
        savedOffer => {
          this.offer = savedOffer;
          this.toastr.success(
            this.translate.instant('EDIT_OFFER.COMPONENT.NEW_OFFER_ADDED'),
            this.translate.instant('EDIT_OFFER.COMPONENT.SUCCESS')
          );
          this.offerForm.reset();
          this.setTemplateToForm();
          this.isSubmitting = false;
        },
        error => {
          LoggerService.log('OfferEditComponent::saveOffer', error);
          this.toastr.error(this.translate.instant('EDIT_OFFER.COMPONENT.OFFER_CREATE_PROBLEM'));
          this.isSubmitting = false;
        }
      );
    }
  }

  openProductDialog(): void {
    this.dialog
      .open(ProductSelectDialogComponent, { minWidth: '1000px', autoFocus: true })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          LoggerService.log('OfferEditComponent::openProductDialog::closeEvent', result);
          this.includedServices.push(result);
        }
      });
  }

  openDiscountDialog(): void {
    this.dialog
      .open(DiscountSelectDialogComponent, { minWidth: '1000px', autoFocus: true })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          LoggerService.log('OfferEditComponent::openDiscountDialog::closeEvent', result);
          this.discounts.push(result);
        }
      });
  }

  openCommentDialog(commentIdx?): void {
    this.dialog
      .open(CommentSelectDialogComponent, { minWidth: '1000px', autoFocus: true })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          LoggerService.log('OfferEditComponent::openCommentDialog::closeEvent', result.comment);
          if (!result.edit) {
            this.comments.push(result.comment);
          } else {
            this.comments[commentIdx] = result.comment;
            this.offerService.comment$.next(null);
          }
        }
      });
  }

  editComment(comment): void {
    const commentIdx = this.comments.indexOf(comment);
    this.offerService.comment$.next(comment);
    this.openCommentDialog(commentIdx);
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/offers/offer-edit/offer-edit.component.ts